import React, { useState } from 'react';
import enquiriesStyles from '../components/enquiries/Enquiries.module.css';

const faqCategories = [
  {
    id: 'account',
    title: 'Profile & Showcase',
    description: 'Managing your profile, gallery, and storefront on Boutee.',
    gradient: 'linear-gradient(135deg, var(--Blue-500) 0%, var(--Blue-600) 100%)',
    questions: [
      {
        id: 'profile-live',
        question: 'How long will it take for my profile to go live?',
        answer:
          'Once you have completed all the onboarding steps, your profile will be reviewed by the Boutee team and made live within 5 business days.',
      },
      {
        id: 'pause-profile',
        question: 'Can I pause my profile if I am too busy?',
        answer:
          'Yes. You can choose to hide your profile on the app from within your portal. This is a great feature to use if you are at max capacity or on leave.',
      },
      {
        id: 'add-gallery',
        question: 'Can I add new work to my gallery?',
        answer:
          'Yes. You can add new showcase pieces and update your gallery at any time from within the portal. Simply navigate to the Products section to add or edit your work.',
      },
      {
        id: 'showcase-limit',
        question: 'Is there a limit to how many showcase pieces I can upload?',
        answer:
          'No, there is no limit. However, we recommend a strong, curated gallery of around 15-25 pieces. This gives customers a great idea of your style without being overwhelming.',
      },
      {
        id: 'edit-showcase',
        question: 'Can I edit my showcase pieces and storefront?',
        answer:
          'Yes. All edits to your showcase pieces and profile can be made directly in the portal. Any changes you make will automatically update on the app, with no need for manual approval.',
      },
    ],
  },
  {
    id: 'customers',
    title: 'Customers & Communication',
    description: 'Managing inquiries, conversations, and customer interactions.',
    gradient: 'linear-gradient(135deg, var(--Amber-500) 0%, var(--Amber-600) 100%)',
    questions: [
      {
        id: 'inquiry-notification',
        question: 'How will I be notified when I get a new inquiry?',
        answer:
          'You will receive a normal email as soon as a customer sends an inquiry. The customer message and any attachments will be delivered straight to your inbox, and a record of the inquiry will also be created in the Conversations section of your portal.',
      },
      {
        id: 'enquiry-response',
        question: 'What do I do when I get an enquiry email?',
        answer:
          'Simply reply to that enquiry email and the customer will see your response. From there, the conversation will go on as normal over email, or feel free to take that customer away onto other communication tools after initially replying to their enquiry email.',
      },
      {
        id: 'communicate-portal',
        question: 'Do I communicate with customers through the portal?',
        answer:
          'No. All communication is done outside the portal, directly with the customer. The portal simply provides you with a centralised place to track the status of your customers, add notes, and manage all your inquiries in one place.',
      },
      {
        id: 'style-profile',
        question: 'Will I be able to see the customer inspiration images before I respond?',
        answer:
          'Yes. When a customer sends an inquiry, they can attach their inspiration links, images, and other details. This information will be available to you on the conversation page so you can get a great head start.',
      },
      {
        id: 'update-status',
        question: 'Am I required to update the customer status?',
        answer:
          'Yes. It is really important that you update a customer status as a conversation progresses. This helps us know how things are going, so we can ensure every customer is getting the attention they need. If we see that a customer status has not been updated for an extended period of time, we will get in touch to make sure everything is okay.',
      },
      {
        id: 'enquiries-projects',
        question: 'What is the relationship between the Enquiries and Projects page?',
        answer:
          'The Enquiries page is for all potential leads that have reached out to you, while the Projects page is for customers where a sale has been agreed. The Enquiries page is designed for managing initial conversations and tracking all new inquiries. The Projects page is where you manage customers once they have agreed to a commission and are ready to move forward. This separation makes it easier to keep track of your active pipeline and ensures you can focus on real projects without distractions.',
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Finance',
    description: 'Commission, invoicing, and getting paid for your work.',
    gradient: 'linear-gradient(135deg, var(--Green-500) 0%, var(--Green-600) 100%)',
    questions: [
      {
        id: 'commission-calculation',
        question: 'How is commission calculated?',
        answer:
          'A full breakdown of our commission structure can be found in the Documents page. You can also use the Quick Fee Check tool on the Calculator page to see your take and Boutee commission for any given price.',
      },
      {
        id: 'invoice-commission',
        question: 'Will the invoicing tool automatically account for Boutee commission?',
        answer:
          'The tool will not automatically account for our commission, so you need to make sure the amount you invoice the customer for includes Boutee commission and any card fees. You can use your Quick Fee Check tool to confirm you are happy with the final amount before you send it. Once the invoice is paid by the customer, we will take our commission and automatically send the remaining funds to your Stripe Connect account.',
      },
      {
        id: 'customer-not-right',
        question: 'What do I do if I feel the customer is not right for me?',
        answer:
          'In this case, please just let us know at ethan@boutee.co.uk, and we will find a different jeweller for the customer. Please let us know at your earliest convenience.',
      },
      {
        id: 'get-paid',
        question: 'How will I get paid for a successful commission?',
        answer:
          'You will receive your funds once the customer has paid the invoice you send through the portal. The money will be automatically transferred to your connected Stripe account.',
      },
      {
        id: 'invoicing-issue',
        question: 'What do I do if I have an issue with the invoicing tool?',
        answer:
          'If you experience any issues with the invoicing tool or any other feature, please email us at ethan@boutee.co.uk or billy@boutee.co.uk. You can also call Ethan directly at 07585006610.',
      },
    ],
  },
];

const FAQ = () => {
  const [activeCategoryId, setActiveCategoryId] = useState(
    faqCategories[0]?.id || ''
  );
  const [openQuestionId, setOpenQuestionId] = useState(null);

const handleCategoryClick = (categoryId) => {
  setActiveCategoryId(categoryId);

  const section = document.getElementById(`faq-section-${categoryId}`);
  if (section) {
    const rect = section.getBoundingClientRect();
    const offset = 120; // adjust if needed based on your header+sticky nav height
    const scrollTop = window.scrollY + rect.top - offset;

    window.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }
};

  const toggleQuestion = (id) => {
    setOpenQuestionId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="animate-fadeIn">
      {/* Header now uses the SAME styles as Enquiries */}
      <div className={enquiriesStyles.header}>
        <div className={enquiriesStyles.headerContent}>
          <div className={enquiriesStyles.headerText}>
            <h1 className={enquiriesStyles.headerTitle}>
              Frequently asked questions
            </h1>
            <p className={enquiriesStyles.headerSubtitle}>
              Find answers about using your Jeweller Portal and interacting with
              Boutee.
            </p>
          </div>
        </div>
      </div>

      {/* Category buttons row - styled like Enquiries filter pills */}
      <div
        style={{
          marginBottom: 'var(--spacing-l)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            gap: 0,
            background: 'var(--Grey-50)',
            padding: '4px',
            borderRadius: 'var(--radius-full)',
          }}
        >
          {faqCategories.map((category) => {
            const isActive = category.id === activeCategoryId;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleCategoryClick(category.id)}
                className="transition-all"
                style={{
                  padding: 'var(--spacing-xs) var(--spacing-m)',
                  background: isActive ? 'var(--Grey-900)' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-full)',
                  color: isActive ? 'var(--Text-Contrast)' : 'var(--Text-Secondary)',
                  fontSize: 'var(--font-size-body4)',
                  fontWeight: 'var(--font-weight-normal)',
                  cursor: 'pointer',
                  transition: 'var(--transition-all)',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xxs)',
                  position: 'relative',
                  boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--Text-Primary)';
                  } else {
                    e.currentTarget.style.background = 'var(--Grey-800)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'var(--Text-Secondary)';
                  } else {
                    e.currentTarget.style.background = 'var(--Grey-900)';
                  }
                }}
              >
                {category.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* FAQ sections */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-l)',
          marginBottom: 'var(--spacing-xxl)',
        }}
      >
        {faqCategories.map((category) => (
          <section
            key={category.id}
            id={`faq-section-${category.id}`}
            style={{
              position: 'relative',
              backgroundColor: 'var(--Surface-Primary)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-xxl)',
              border: '1px solid var(--Stroke-Light)',
              boxShadow: 'var(--shadow-frame-small)',
              overflow: 'hidden',
            }}
          >
            {/* Decorative gradient accent strip */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: category.gradient
            }} />
            {/* Section header */}
            <div
              style={{
                marginBottom: 'var(--spacing-l)',
              }}
            >
              <h2
                style={{
                  fontSize: 'var(--font-size-h5)',
                  fontWeight: 'var(--font-weight-h4)',
                  color: 'var(--Text-Primary)',
                  marginBottom: 'var(--spacing-xs)',
                }}
              >
                {category.title}
              </h2>
              {category.description && (
                <p
                  style={{
                    fontSize: 'var(--font-size-body4)',
                    color: 'var(--Text-Secondary)',
                  }}
                >
                  {category.description}
                </p>
              )}
            </div>

            {/* Questions */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-s)',
              }}
            >
              {category.questions.map((q) => {
                const isOpen = openQuestionId === q.id;

                return (
                  <article
                    key={q.id}
                    className="group"
                    style={{
                      borderRadius: 'var(--radius-m)',
                      border: '1px solid var(--Stroke-Light)',
                      backgroundColor: 'var(--Surface-Primary)',
                      boxShadow: 'var(--shadow-frame-small)',
                      overflow: 'hidden',
                      transition:
                        'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow =
                        'var(--shadow-frame-big)';
                      e.currentTarget.style.borderColor = 'var(--Purple-300)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        'var(--shadow-frame-small)';
                      e.currentTarget.style.borderColor =
                        'var(--Stroke-Light)';
                    }}
                  >
                    {/* Question header */}
                    <button
                      type="button"
                      onClick={() => toggleQuestion(q.id)}
                      className="w-full flex justify-between items-center"
                      style={{
                        padding: 'var(--spacing-m) var(--spacing-l)',
                        backgroundColor: 'var(--Surface-Secondary)',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-body3)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--Text-Primary)',
                          paddingRight: 'var(--spacing-s)',
                        }}
                      >
                        {q.question}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-h5)',
                          color: 'var(--Text-Secondary)',
                        }}
                      >
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>

                    {/* Answer panel */}
                    <div
                      style={{
                        maxHeight: isOpen ? '300px' : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.2s ease',
                        backgroundColor: 'var(--Surface-Primary)',
                      }}
                    >
                      <div
                        style={{
                          padding:
                            'var(--spacing-m) var(--spacing-l) var(--spacing-l)',
                          fontSize: 'var(--font-size-body4)',
                          color: 'var(--Text-Secondary)',
                          lineHeight: 'var(--line-height-body2)',
                        }}
                      >
                        {q.answer}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default FAQ;